import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export function AuthCallback() {
  const { googleSession } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { navigate("/login"); return; }
    googleSession(sid).then(() => navigate("/")).catch(() => { toast.error("Connexion Google échouée"); navigate("/login"); });
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
    </div>
  );
}

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => { if (user) navigate("/"); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(location.state?.from?.pathname || "/");
      return;
    } catch (err) {
      if (err?.response?.status === 401) {
        // Porte d'entrée unique : tenter aussi le compte client Graphisme
        try {
          await api.post("/graphisme/auth/login", { email: form.email.trim(), password: form.password });
          navigate("/graphisme/espace");
          return;
        } catch { /* identifiants incorrects des deux côtés */ }
        setError("Identifiants incorrects");
      } else {
        setError(apiError(err));
      }
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setLoading(true);
    try {
      // Compte admin DGA et/ou compte client Graphisme — réponse volontairement identique
      await Promise.allSettled([
        api.post("/auth/forgot-password", { email: forgotEmail.trim() }),
        api.post("/graphisme/auth/forgot", { email: forgotEmail.trim() }),
      ]);
      setForgotSent(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="bg-white border border-neutral-200 rounded-2xl shadow-sm p-8">
          <div className="flex flex-col items-center text-center mb-8">
            <img src="/logo-dga.png" alt="DGA" className="w-14 h-14 rounded-xl object-cover mb-4" data-testid="login-logo" />
            <h1 className="font-heading text-xl font-bold" data-testid="auth-title">Connectez-vous à votre espace de travail.</h1>
          </div>

          {!forgot ? (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Email</Label>
                <Input data-testid="auth-email" type="email" className="mt-1.5" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input data-testid="auth-password" type="password" className="mt-1.5" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} autoComplete="current-password" />
              </div>
              <div className="text-right">
                <button type="button" onClick={() => { setForgot(true); setForgotEmail(form.email); setError(""); }}
                  className="text-xs text-neutral-500 hover:text-neutral-900 transition-colors" data-testid="auth-forgot">
                  Mot de passe oublié ?
                </button>
              </div>
              {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
              <Button data-testid="auth-submit" type="submit" disabled={loading}
                className="w-full bg-neutral-900 hover:bg-neutral-800 text-white h-10">
                {loading ? "Chargement…" : "Se connecter"}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitForgot} className="space-y-3" data-testid="forgot-form">
              {forgotSent ? (
                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="forgot-success">
                  Si un compte est associé à cette adresse, un email avec un lien sécurisé vient d'être envoyé.
                </div>
              ) : (
                <>
                  <p className="text-sm text-neutral-500">Indiquez votre email — vous recevrez un lien sécurisé pour choisir un nouveau mot de passe.</p>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="forgot-email" type="email" className="mt-1.5" value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)} required autoComplete="email" />
                  </div>
                  <Button data-testid="forgot-submit" type="submit" disabled={loading || !forgotEmail.trim()}
                    className="w-full bg-neutral-900 hover:bg-neutral-800 text-white h-10">
                    {loading ? "Envoi…" : "Recevoir le lien"}
                  </Button>
                </>
              )}
              <button type="button" onClick={() => { setForgot(false); setForgotSent(false); }}
                className="w-full text-xs text-neutral-400 hover:text-neutral-900 transition-colors" data-testid="forgot-back">
                ← Retour à la connexion
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
